import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { create } from 'zustand';
import { assert } from '../errors';
import {
    AllFinancialEntitiesDocument,
    AtomicStatusDocument,
    CurrentUserProfileDocument,
    FinancialEntityDocument,
    GoalDocument,
    NowDocument,
    RecentPushEventsDocument,
    TransactionEntriesFilteredDocument,
    TransactionEntryCategoriesDocument,
} from '../graphql/generated';
import { PushEventData } from '../push';
import { dt } from '../util/dateTime';

type ApolloClientType = ApolloClient<NormalizedCacheObject>;
type PushEventHandlerFn = (pushData: PushEventData) => void;
type PushEventStore = {
    setApolloClient: (client: ApolloClientType) => void;
    apolloClient: ApolloClientType | null;
    pageHandlers: Record<string, PushEventHandlerFn>;
    addPageHandler: (pageId: string, handler: PushEventHandlerFn) => void;
    removePageHandler: (pageId: string) => void;
    handlePushEvent: PushEventHandlerFn;
};

// This can live outside because we'll never want to handle the same push event twice.
const handledPushEventIds = new Set<string>();

export const usePushEventPoller = (apolloClient: ApolloClientType) => {
    const { data: nowData } = useQuery(NowDocument);
    const handlePushEvent = usePushEventStore((state) => state.handlePushEvent);
    const setApolloClient = usePushEventStore((state) => state.setApolloClient);

    useEffect(() => {
        setApolloClient(apolloClient);
    }, [apolloClient, setApolloClient]);

    useEffect(() => {
        // Establish a cursor (ISO8601 date string) to avoid refetching old events
        let cursor = nowData?.now ?? null;

        const interval = setInterval(() => {
            if (!cursor) return;

            // Add a buffer to ensure we don't miss events due to transaction timing issues
            // Ref: https://withplenty.slack.com/archives/C02M89B8M6E/p1740611192497369
            const cursorWithBuffer = dt(cursor)
                .subtract(5, 'minutes')
                .toISOString();

            apolloClient
                .query({
                    query: RecentPushEventsDocument,
                    variables: { createdAfter: cursorWithBuffer },
                    // Execute the query without first checking the cache.
                    // The query's result is NOT stored in the cache.
                    fetchPolicy: 'no-cache',
                })
                .then((result) => {
                    const events = result.data.recentPushEvents;
                    if (!events?.length) return;

                    // Use the most recent timestamp as the cursor in subsequent queries
                    // Obviously not the most efficient method, but good enough for now
                    cursor = events
                        .map(({ createdAt }) => createdAt)
                        .sort()
                        .reverse()[0];

                    events.forEach((event) => {
                        assert(cursor);

                        if (event.createdAt > cursor) {
                            cursor = event.createdAt;
                        }

                        if (handledPushEventIds.has(event.id)) {
                            return;
                        }

                        handledPushEventIds.add(event.id);

                        if (handledPushEventIds.size > 100) {
                            const nextValue = handledPushEventIds
                                .values()
                                .next().value;
                            if (nextValue) {
                                handledPushEventIds.delete(nextValue);
                            }
                        }

                        handlePushEvent(event.data);
                    });
                });
        }, 5000);

        return () => clearInterval(interval);
    }, [nowData, handlePushEvent, apolloClient]);
};

export const usePushEventHandler = (
    pageId: string,
    handler: PushEventHandlerFn
) => {
    const addPageHandler = usePushEventStore((state) => state.addPageHandler);
    const removePageHandler = usePushEventStore(
        (state) => state.removePageHandler
    );

    useEffect(() => {
        addPageHandler(pageId, handler);
        return () => removePageHandler(pageId);
    }, [pageId, handler, addPageHandler, removePageHandler]);
};

const usePushEventStore = create<PushEventStore>((set, get) => ({
    apolloClient: null,
    setApolloClient: (apolloClient) => set({ apolloClient }),
    pageHandlers: {},
    addPageHandler: (pageId, handler) =>
        set((state) => ({
            pageHandlers: { ...state.pageHandlers, [pageId]: handler },
        })),
    removePageHandler: (pageId) =>
        set((state) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [pageId]: _, ...rest } = state.pageHandlers;
            return { pageHandlers: rest };
        }),
    handlePushEvent: (pushData) => {
        const { apolloClient } = get();

        assert(apolloClient, 'Apollo client not set in PushEventStore');

        // Global handling logic
        switch (pushData.type) {
            case 'MEMBERSHIP_UPDATED':
                apolloClient.query({
                    query: CurrentUserProfileDocument,
                    fetchPolicy: 'network-only',
                });
                break;

            case 'FINANCIAL_ENTITY_UPDATED': {
                if (pushData.financialEntityTypename === 'PlentyGoalAccount') {
                    // Ignore; handled by GOAL_UPDATED
                    break;
                } else {
                    apolloClient.query({
                        query: FinancialEntityDocument,
                        variables: {
                            id: pushData.financialEntityId,
                        },
                        fetchPolicy: 'network-only',
                    });
                }
                break;
            }

            case 'FINANCIAL_ENTITIES_UPDATED': {
                apolloClient.query({
                    query: AllFinancialEntitiesDocument,
                    fetchPolicy: 'network-only',
                });
                break;
            }

            case 'USER_PROFILE_UPDATED': {
                apolloClient.query({
                    query: CurrentUserProfileDocument,
                    fetchPolicy: 'network-only',
                });
                break;
            }

            case 'GOAL_UPDATED': {
                apolloClient.query({
                    query: GoalDocument,
                    variables: {
                        id: pushData.goalId,
                    },
                    fetchPolicy: 'network-only',
                });
                break;
            }

            case 'TRANSACTION_UPDATED': {
                // TODO: Make this surgical instead of refetching the world.
                apolloClient.query({
                    query: AllFinancialEntitiesDocument,
                    fetchPolicy: 'network-only',
                });
                break;
            }

            case 'ATOMIC_INDIVIDUAL_ACCOUNT_CONFIRMED': {
                apolloClient.query({
                    query: AtomicStatusDocument,
                    fetchPolicy: 'network-only',
                });
                apolloClient.query({
                    query: CurrentUserProfileDocument,
                    fetchPolicy: 'network-only',
                });
                break;
            }

            case 'TRANSACTION_ENTRY_CATEGORIES_UPDATED': {
                // These need to be refetched in sequence to avoid a race condition
                // For example:
                // If P1 updates a transaction entry with a brand new category,
                // and P2 receives this push event, we need to ensure that the
                // new categories is fetched before the transaction entries are refetched
                apolloClient
                    .refetchQueries({
                        include: [TransactionEntryCategoriesDocument],
                    })
                    .then(() =>
                        apolloClient.refetchQueries({
                            include: [TransactionEntriesFilteredDocument],
                        })
                    );
                break;
            }
        }

        // Call all page-specific handlers
        Object.values(get().pageHandlers).forEach((handler) =>
            handler(pushData)
        );
    },
}));
